import { WireAnalyticsEvent } from 'scenes/common/types';
import * as H from 'history';
import { RoutePaths } from '../routes';
import {
  getCurrentHttpErrorCode,
  getIsSystemErrorState,
  getPaymentMethod,
  getPolicyNumber,
  getProductDescription,
  getProductType,
  getRiskPostcode,
  getPaymentType,
  getEffectiveDate,
  getContextErrorOccuredAt,
} from '../redux/payment/selectors';
import { PaymentState, RenewalStatus } from '../types';
import { getPremiumInfo } from '../components/Steps/PolicyPayment/PolicyPayment';

const MILLI_PER_DAY = 1000 * 60 * 60 * 24;

// Helper functions
const constructPageName = (step: string) => `gi:payment:${step}`;

const getProductSubType = (productType: string, subType: string) => {
  if (!productType || !subType) {
    return '';
  }
  return `${productType}:${subType}`;
};

const yearlyOrMonthlyPayment = (state: PaymentState) => {
  const paymentType = getPaymentType({ payment: state });
  if (!paymentType) {
    return '';
  }
  return paymentType === RenewalStatus.Renewal ? 'yearly' : 'monthly';
};

const paymentType = (state: PaymentState) => {
  const type = getPaymentType({ payment: state });
  if (!type) {
    return '';
  }
  return type === RenewalStatus.Renewal ? 'Renewal' : 'Fail payment';
};

const daysToPolicy = (state: PaymentState) => {
  const effectiveDate = getEffectiveDate({ payment: state });
  if (!effectiveDate) {
    return '';
  }
  const startDate = new Date(effectiveDate);
  const currentDate = new Date();

  const diff = startDate.getTime() - currentDate.getTime();
  return Math.ceil(diff / MILLI_PER_DAY);
};

const toStepName = (path: string) => {
  // Spec is to return 'landing-page' on the first payment-portal page
  // and the nested page name (i.e pay for /payments/pay) for subsequent pages
  const updatedPath = path.replace(RoutePaths.LANDING, '');
  return !updatedPath ? 'landing-page' : updatedPath.replace('/', '');
};

const constructErrorMessage = (
  callingContext: string,
  validationMessage?: string
) =>
  validationMessage
    ? `${callingContext} fail. ${validationMessage}`
    : `${callingContext} fail.`;

// Object marshallers
const toFormPage = (state: PaymentState) => {
  const { price } = getPremiumInfo(state);
  return {
    quoteNumber: getPolicyNumber({ payment: state }),
    quoteAmountWithExtras: price !== '0' ? price : '',
    productType: getProductType({ payment: state }).toLocaleLowerCase(),
    productSubType: getProductSubType(
      getProductType({ payment: state }).toLocaleLowerCase(),
      getProductDescription({ payment: state })
    ),
    daysToPolicy: daysToPolicy(state),
    flybuysStatus: null,
    excessAmount: '',
    contentExcessAmount: '',
    buildingExcessAmount: '',
    renewalType: paymentType(state),
    riskPostcode: getRiskPostcode({ payment: state }),
    payment: {
      method: getPaymentMethod({ payment: state }),
      terms: yearlyOrMonthlyPayment(state),
    },
  };
};

const toWirePage = (step: string) => ({
  pageUrl: window.location.href,
  siteSection: 'payment',
  siteServer: window.location.hostname,
  stepName: `payment:renewal:${step}`,
  pageName: constructPageName(step),
  timeStamp: `${new Date().valueOf()}`,
});

const toError = (
  location: H.Location,
  state: PaymentState,
  uiMessage?: string
) => ({
  code: getCurrentHttpErrorCode({ payment: state }),
  type: getIsSystemErrorState({ payment: state }) ? 'technical' : 'business',
  pageName: constructPageName(toStepName(location.pathname)),
  message: constructErrorMessage(
    getContextErrorOccuredAt({ payment: state }),
    uiMessage
  ),
});

// Event creators
export const defaultEvent = (
  location: H.Location,
  state: PaymentState,
  eventType: WireAnalyticsEvent
) => ({
  event: eventType,
  page: toWirePage(toStepName(location.pathname)),
  formData: toFormPage(state),
});

export const toViewLoadedEvent = (
  location: H.Location,
  state: PaymentState,
  error?: boolean
) =>
  !error
    ? defaultEvent(location, state, WireAnalyticsEvent.ViewLoaded)
    : {
        ...defaultEvent(location, state, WireAnalyticsEvent.ViewLoaded),
        error: toError(location, state),
      };

export const toErrorEvent = (
  location: H.Location,
  state: PaymentState,
  uiMessage?: string
) => ({
  ...defaultEvent(location, state, WireAnalyticsEvent.Error),
  error: toError(location, state, uiMessage),
});
